// Traceability: FR-/AS-/EC- IDs reference
// .verified/features/dashboard-layout-editor/spec.md; ADR-022 is the grid model.
// Approach A: the FE persists only the sections/hidden blob; the view name lives
// in the authoritative `layout_name` column.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { LayoutItem } from "react-grid-layout/legacy";

import { Convert } from "@/models/responses";
import type { DashboardLayout } from "@/models/responses";
import { errorPopup, successPopup } from "@/utils/alerts";
import { decodeErrorResponse } from "@/utils/errors";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";

import type { DashboardView, SectionDef, TileDef } from "./types";
import { reconcile } from "./reconcile";

const DEFAULT_VIEW_NAME = "Default";

function activeKey(dashboardId: string): string {
    return `dashboardLayout.${dashboardId}.selectedViewId`;
}

/**
 * Build the code-default arrangement for the empty Default(0) view: every
 * registry tile placed at its `defaultLayout`, nothing hidden. This is the
 * canonical "reset" target.
 */
function defaultArrangement(
    sections: SectionDef[],
    registry: TileDef[],
): { sections: Record<string, LayoutItem[]>; hidden: string[] } {
    const { layouts, hidden } = reconcile(
        { id: 0, name: DEFAULT_VIEW_NAME, sections: {}, hidden: [] },
        sections,
        registry,
        12,
    );
    return { sections: layouts, hidden: Array.from(hidden) };
}

/**
 * Boundary cast: opaque server `layout` blob → concrete {@link DashboardView}.
 * Name comes from the `layout_name` column, not the blob. Malformed blobs are
 * default-guarded through reconcile (EC-03) so a corrupt row never throws.
 */
function toDashboardView(
    layout: DashboardLayout,
    sections: SectionDef[],
    registry: TileDef[],
): DashboardView {
    const blob = (layout.layout ?? {}) as {
        sections?: unknown;
        hidden?: unknown;
    };
    const raw: DashboardView = {
        id: layout.id,
        name: layout.layoutName,
        sections: (blob.sections as Record<string, LayoutItem[]>) ?? {},
        hidden: Array.isArray(blob.hidden) ? (blob.hidden as string[]) : [],
    };
    const { layouts, hidden } = reconcile(raw, sections, registry, 12);
    return { id: layout.id, name: layout.layoutName, sections: layouts, hidden: Array.from(hidden) };
}

/** Stable serialisation of a view's persisted arrangement for deep compare. */
function arrangementKey(view: Pick<DashboardView, "sections" | "hidden">): string {
    return JSON.stringify({ sections: view.sections, hidden: [...view.hidden].sort() });
}

/** The blob the FE persists — `sections`/`hidden` only; name is a write-only no-op here. */
function toLayoutBlob(view: Pick<DashboardView, "sections" | "hidden">): {
    [key: string]: unknown;
} {
    return { sections: view.sections, hidden: view.hidden };
}

export interface UseDashboardLayout {
    views: DashboardView[];
    activeViewId: number;
    activeView: DashboardView;
    editMode: boolean;
    modified: boolean;
    working: DashboardView;
    enterEdit: () => void;
    cancel: () => void;
    save: () => Promise<void>;
    saveAs: (name: string) => Promise<void>;
    reset: () => void;
    del: () => Promise<void>;
    switchView: (id: number) => void;
    setWorking: (view: DashboardView) => void;
}

export function useDashboardLayout(
    dashboardId: string,
    sections: SectionDef[],
    registry: TileDef[],
): UseDashboardLayout {
    // The code-defined Default(0) view: always present, always first.
    const defaultView = useMemo<DashboardView>(() => {
        const arr = defaultArrangement(sections, registry);
        return { id: 0, name: DEFAULT_VIEW_NAME, sections: arr.sections, hidden: arr.hidden };
    }, [sections, registry]);

    const [views, setViews] = useState<DashboardView[]>([defaultView]);
    const [activeViewId, setActiveViewId] = useState<number>(0);
    const [editMode, setEditMode] = useState(false);
    const [working, setWorkingState] = useState<DashboardView>(defaultView);

    // The raw server rows, kept so the loaded views can be RE-reconciled against the
    // current registry. The page builds its registry from async data (booking-pace
    // stats), so on mount the registry can be empty; without this re-reconciliation a
    // view's `hidden`/`sections` would be intersected against an empty registry and
    // silently dropped (FR-20). Re-reconciled whenever sections/registry change.
    const rawLayouts = useRef<DashboardLayout[]>([]);

    const activeView = useMemo<DashboardView>(
        () => views.find((v) => v.id === activeViewId) ?? defaultView,
        [views, activeViewId, defaultView],
    );

    // Keep `working` seeded from the active view whenever the active view changes
    // and we are NOT mid-edit (e.g. after switch / load). Seeding from a copy so
    // setWorking mutations never alias the saved view.
    const seededFor = useRef<number | null>(null);
    useEffect(() => {
        if (editMode) return;
        if (seededFor.current === activeViewId && working.id === activeViewId) return;
        seededFor.current = activeViewId;
        setWorkingState({ ...activeView });
    }, [activeViewId, activeView, editMode, working.id]);

    // Load named views on mount (fetch + Convert, mirroring Grid.tsx).
    useEffect(() => {
        let cancelled = false;
        fetch(`${apiUrl}${getApiBasePath()}/dashboard/layouts?dashboardId=${dashboardId}`, fetchOptions)
            .then((response) => {
                if (!response.ok) return decodeErrorResponse(response);
                return response.text();
            })
            .then((text) => {
                if (cancelled) return;
                const serverLayouts = Convert.toGetDashboardLayoutsResponse(text).layouts;
                rawLayouts.current = serverLayouts;
                const serverViews = serverLayouts.map((l) => toDashboardView(l, sections, registry));
                const loaded = [defaultView, ...serverViews];
                setViews(loaded);

                const stored = localStorage.getItem(activeKey(dashboardId));
                const storedId = stored === null ? 0 : Number(stored);
                const resolved = loaded.some((v) => v.id === storedId) ? storedId : 0;
                setActiveViewId(resolved);
                const active = loaded.find((v) => v.id === resolved) ?? defaultView;
                seededFor.current = resolved;
                setWorkingState({ ...active });
            })
            .catch((error: unknown) => {
                void errorPopup(error instanceof Error ? error.message : String(error));
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardId]);

    // A stable signature of the section + tile id sets. Callers may pass fresh
    // `sections`/`registry` array instances each render (identity churn) — keying the
    // re-reconcile on the id-set CONTENT (not array identity) prevents an update loop.
    const registrySignature = useMemo(
        () => `${sections.map((s) => s.id).join(",")}|${registry.map((t) => t.id).join(",")}`,
        [sections, registry],
    );
    const reconciledSignature = useRef<string | null>(null);

    // Re-reconcile the loaded views against the CURRENT registry whenever the section/
    // tile id set changes (e.g. the page's registry populated after its async stats
    // arrived). Skipped while editing so an in-flight working arrangement is never
    // clobbered. Idempotent: no raw rows → no-op; same signature → no-op.
    useEffect(() => {
        if (editMode) return;
        if (rawLayouts.current.length === 0) return;
        if (reconciledSignature.current === registrySignature) return;
        reconciledSignature.current = registrySignature;
        const reconciled = [defaultView, ...rawLayouts.current.map((l) => toDashboardView(l, sections, registry))];
        setViews(reconciled);
        const active = reconciled.find((v) => v.id === activeViewId) ?? defaultView;
        seededFor.current = activeViewId;
        setWorkingState({ ...active });
        // `defaultView` already folds in sections/registry; activeViewId is read to
        // re-seed working from the freshly reconciled active view.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registrySignature, editMode]);

    const savedActive = useMemo(() => views.find((v) => v.id === activeViewId), [views, activeViewId]);
    const modified = useMemo(() => {
        if (!savedActive) return false;
        return arrangementKey(working) !== arrangementKey(savedActive);
    }, [working, savedActive]);

    const setWorking = useCallback((view: DashboardView) => {
        setWorkingState(view);
    }, []);

    const switchView = useCallback(
        (id: number) => {
            setActiveViewId(id);
            localStorage.setItem(activeKey(dashboardId), String(id));
            setEditMode(false);
        },
        [dashboardId],
    );

    const enterEdit = useCallback(() => {
        setWorkingState({ ...activeView });
        setEditMode(true);
    }, [activeView]);

    const cancel = useCallback(() => {
        setWorkingState({ ...activeView });
        setEditMode(false);
    }, [activeView]);

    const postLayout = useCallback(
        async (id: number, name: string, view: Pick<DashboardView, "sections" | "hidden">): Promise<DashboardLayout> => {
            const body = Convert.upsertDashboardLayoutRequestToJson({
                id,
                dashboardId,
                layoutName: name,
                layout: toLayoutBlob({ sections: view.sections, hidden: view.hidden }),
            });
            const response = await fetch(`${apiUrl}${getApiBasePath()}/dashboard/layouts`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body,
            });
            if (!response.ok) {
                await decodeErrorResponse(response);
            }
            const text = await response.text();
            return Convert.toUpsertDashboardLayoutResponse(text).layout;
        },
        [dashboardId],
    );

    const save = useCallback(async () => {
        // Default(0) cannot be updated in place — there is no server row. Saving
        // changes to Default must go through Save As (handled at the toolbar).
        if (activeViewId === 0) {
            void errorPopup("Use Save As to store changes to the Default view.");
            return;
        }
        try {
            const saved = await postLayout(activeViewId, activeView.name, working);
            rawLayouts.current = rawLayouts.current.map((l) => (l.id === saved.id ? saved : l));
            const view = toDashboardView(saved, sections, registry);
            setViews((prev) => prev.map((v) => (v.id === view.id ? view : v)));
            setWorkingState({ ...view });
            setEditMode(false);
        } catch (error: unknown) {
            void errorPopup(error instanceof Error ? error.message : String(error));
        }
    }, [activeViewId, activeView, working, postLayout, sections, registry]);

    const saveAs = useCallback(
        async (name: string) => {
            const trimmed = name.trim();
            if (trimmed === "") {
                void errorPopup("Please enter a view name.");
                return;
            }
            if (trimmed === DEFAULT_VIEW_NAME) {
                void errorPopup('"Default" is reserved.');
                return;
            }
            if (views.some((v) => v.name === trimmed)) {
                void errorPopup(`A view named "${trimmed}" already exists.`);
                return;
            }
            try {
                const saved = await postLayout(0, trimmed, working);
                rawLayouts.current = [...rawLayouts.current, saved];
                const view = toDashboardView(saved, sections, registry);
                setViews((prev) => [...prev, view]);
                setActiveViewId(view.id);
                localStorage.setItem(activeKey(dashboardId), String(view.id));
                setWorkingState({ ...view });
                setEditMode(false);
                void successPopup("View saved.");
            } catch (error: unknown) {
                void errorPopup(error instanceof Error ? error.message : String(error));
            }
        },
        [views, working, postLayout, sections, registry, dashboardId],
    );

    const del = useCallback(async () => {
        if (activeViewId === 0) {
            void errorPopup("The Default view cannot be deleted.");
            return;
        }
        const deletingId = activeViewId;
        try {
            const response = await fetch(`${apiUrl}${getApiBasePath()}/dashboard/layouts/${deletingId}`, {
                method: "DELETE",
                credentials: "include",
            });
            if (!response.ok) {
                await decodeErrorResponse(response);
            }
            rawLayouts.current = rawLayouts.current.filter((l) => l.id !== deletingId);
            const remaining = views.filter((v) => v.id !== deletingId);
            setViews(remaining);
            const fallback = remaining[0] ?? defaultView;
            setActiveViewId(fallback.id);
            localStorage.setItem(activeKey(dashboardId), String(fallback.id));
            setWorkingState({ ...fallback });
            setEditMode(false);
            void successPopup("View deleted.");
        } catch (error: unknown) {
            void errorPopup(error instanceof Error ? error.message : String(error));
        }
    }, [activeViewId, views, defaultView, dashboardId]);

    const reset = useCallback(() => {
        const arr = defaultArrangement(sections, registry);
        setWorkingState((prev) => ({ ...prev, sections: arr.sections, hidden: arr.hidden }));
    }, [sections, registry]);

    return {
        views,
        activeViewId,
        activeView,
        editMode,
        modified,
        working,
        enterEdit,
        cancel,
        save,
        saveAs,
        reset,
        del,
        switchView,
        setWorking,
    };
}
